/**
 * Monaco-backed JSON editor for catalog gateway payloads.
 *
 * The schema passed here is purely indicative (autocomplete + hover docs +
 * live diagnostics) ; the catalog itself enforces structure at deploy time.
 * The editor never rejects input — it only surfaces problems and lets the
 * parent disable the Save button when ``onValidity`` reports a syntax issue.
 *
 * Monaco is lazy-loaded so it does not bloat the rest of the bundle: the
 * Catalog page only enters this code path when an operator explicitly
 * opens the editor.
 */

import { Suspense, lazy, useEffect, useId, useRef } from "react";
import type {
  Monaco,
  OnChange,
  OnMount,
  OnValidate,
} from "@monaco-editor/react";

import { Spinner } from "@/components/Spinner";

const MonacoEditor = lazy(() =>
  import("@monaco-editor/react").then((m) => ({ default: m.default })),
);

export interface JsonPayloadEditorProps {
  value: string;
  onChange: (value: string) => void;
  /**
   * Indicative JSON Schema. Pass ``undefined`` when the backend has no
   * schema for the current kind/section — the editor will fall back to
   * pure JSON syntax checking.
   */
  schema?: Record<string, unknown>;
  /**
   * Fires whenever the syntax-validity of the document changes. The
   * parent typically disables the submit button while this is false.
   */
  onValidity?: (valid: boolean) => void;
  /** Minimum editor height in px. Defaults to 400. */
  minHeight?: number;
  /**
   * Locks the document against edits. Defaults to ``false`` — every
   * existing caller (``CatalogGatewayEdit``, and the capability/runtime
   * blocks of ``CatalogComponentEdit``) keeps writing.
   *
   * Set on Monaco itself (``options={{ readOnly: true }}``), not just
   * withheld from ``onChange``: Monaco keeps its own text model regardless
   * of what the parent does with ``onChange``, so disabling only the React
   * side would still let an operator type into the widget — a field that
   * *looks* protected without being one.
   */
  readOnly?: boolean;
  /**
   * The editor's accessible name. Monaco draws its own widget, so a visible
   * `<label>` next to it names nothing: this is what assistive technology
   * reads when the editor takes focus. Repeat the visible heading here.
   */
  ariaLabel?: string;
}

// Monaco's JSON language service maintains a *single* schemas array per
// page (``jsonDefaults.setDiagnosticsOptions`` replaces it wholesale).
// Calling it from each editor instance with its own one-element array
// would clobber the schemas registered by sibling editors. We therefore
// maintain a module-scoped registry keyed by URI and always re-emit the
// aggregated list. This makes the helper safe for any future use case
// that mounts more than one ``JsonPayloadEditor`` at once (compare modal,
// diff view, side-by-side preview…).
interface SchemaRegistration {
  uri: string;
  fileMatch: string[];
  schema: Record<string, unknown>;
}
const _schemaRegistry = new Map<string, SchemaRegistration>();

function _flushSchemas(monaco: Monaco) {
  monaco.languages.json.jsonDefaults.setDiagnosticsOptions({
    validate: true,
    allowComments: false,
    enableSchemaRequest: false,
    schemas: Array.from(_schemaRegistry.values()),
  });
}

function registerSchema(
  monaco: Monaco,
  uri: string,
  schema: Record<string, unknown> | undefined,
) {
  if (schema) {
    _schemaRegistry.set(uri, { uri, fileMatch: [uri], schema });
  } else {
    _schemaRegistry.delete(uri);
  }
  _flushSchemas(monaco);
}

function unregisterSchema(monaco: Monaco | null, uri: string) {
  _schemaRegistry.delete(uri);
  // ``monaco`` is null when the component unmounts before Monaco ever
  // mounted (Suspense fallback path). Nothing to flush in that case.
  if (monaco) _flushSchemas(monaco);
}

function useMonacoSchema(schemaUri: string, schema?: Record<string, unknown>) {
  // Capture the monaco namespace on mount so a schema swap (after a
  // ``kind`` change) can re-register without remounting the editor.
  const monacoRef = useRef<Monaco | null>(null);

  const handleMount: OnMount = (_editor, monaco) => {
    monacoRef.current = monaco;
    registerSchema(monaco, schemaUri, schema);
  };

  useEffect(() => {
    if (!monacoRef.current) return;
    registerSchema(monacoRef.current, schemaUri, schema);
  }, [schemaUri, schema]);

  // Cleanup on unmount: drop our entry from the registry so a stale
  // schema scoped to a non-existent model URI doesn't accumulate. We
  // capture monacoRef in a const to keep the closure stable.
  useEffect(() => {
    return () => unregisterSchema(monacoRef.current, schemaUri);
  }, [schemaUri]);

  return handleMount;
}

export function JsonPayloadEditor({
  value,
  onChange,
  schema,
  onValidity,
  minHeight = 400,
  readOnly = false,
  ariaLabel,
}: JsonPayloadEditorProps) {
  // Stable URI per editor instance — needed so Monaco can scope our
  // schema to *this* editor's model rather than every JSON document.
  const editorId = useId();
  const schemaUri = `inmemory://payload/${editorId}.json`;
  const modelUri = schemaUri;

  const handleMount = useMonacoSchema(schemaUri, schema);

  const handleChange: OnChange = (next) => {
    onChange(next ?? "");
  };

  const handleValidate: OnValidate = (markers) => {
    if (!onValidity) return;
    // Severity 8 = Error in Monaco. Warnings (e.g. schema mismatch) do
    // not block submission — they're indicative ; the catalog enforces
    // structure at deploy time.
    const hasSyntaxError = markers.some((m) => m.severity === 8);
    onValidity(!hasSyntaxError);
  };

  return (
    <div className="rounded border border-line overflow-hidden">
      <Suspense
        fallback={
          <div
            className="flex items-center justify-center bg-sunken"
            style={{ height: minHeight }}
          >
            <Spinner />
          </div>
        }
      >
        <MonacoEditor
          height={minHeight}
          defaultLanguage="json"
          language="json"
          path={modelUri}
          value={value}
          onMount={handleMount}
          onChange={handleChange}
          onValidate={handleValidate}
          options={{
            minimap: { enabled: false },
            scrollBeyondLastLine: false,
            fontSize: 13,
            tabSize: 2,
            formatOnPaste: true,
            formatOnType: true,
            lineNumbers: "on",
            renderWhitespace: "selection",
            wordWrap: "on",
            // The lock itself: Monaco keeps its own text model, so this is
            // what actually refuses keystrokes — see the prop's docstring.
            readOnly,
            ariaLabel,
          }}
        />
      </Suspense>
    </div>
  );
}
